import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Clipboard } from '@capacitor/clipboard';
import { Share } from '@capacitor/share';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { DswToastService } from '@vi-succ/design-system-web';
import { ProductCartDataService, ProductDetail } from '@vi-succ/products-domain';
import {
  DeliveryType,
  PickupLocation,
  ShippingItem,
  ShoppingCart,
  ShoppingCartDataService,
  shoppingCartPriceLoading,
  shoppingCartPriceSuccess,
  shoppingCartReset,
  shoppingCartSuccess,
} from '@vi-succ/shoppingcart-domain';
import dayjs from 'dayjs';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  interval,
  map,
  of,
  switchMap,
  takeUntil,
  takeWhile,
  tap,
  timer,
} from 'rxjs';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { AuthService } from '../core/login/services/auth.service';
import { environment } from '../environments/environment';
import { CartShareFactory } from '../factories/cart-share.factory';
import { createDeepMutableClone } from '../util/object.util';
import { BrowserService } from './browser.service';
import { EtCatalogDataService } from './et-catalog-data.service';
import { StorageService } from './storage.service';

export interface CheckoutState {
  checkoutInProgress: boolean;
}

@Injectable({ providedIn: 'root' })
export class EtShoppingCartDataService implements ShoppingCartDataService, ProductCartDataService {
  private CHECKOUT_STATE_KEY = 'checkout_state';
  private checkoutState$ = new BehaviorSubject<CheckoutState>({ checkoutInProgress: false });
  private pollingSub?: Subscription;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private catalogService: EtCatalogDataService,
    private translateService: TranslateService,
    private browserService: BrowserService,
    private toastService: DswToastService,
    private storage: StorageService,
    private store: Store,
  ) {
    this.checkoutState$.subscribe((checkoutState) => {
      if (checkoutState && checkoutState.checkoutInProgress) {
        this.startCartPolling(environment.checkoutPollingInterval);
      }
    });

    fromPromise(this.getCheckoutState()).subscribe((checkoutState) => {
      this.setCheckoutState(checkoutState);
    });
  }

  getCheckoutState() {
    return this.storage.get<CheckoutState>(this.CHECKOUT_STATE_KEY);
  }

  setCheckoutState(state: CheckoutState) {
    this.storage.set(this.CHECKOUT_STATE_KEY, state);
    this.checkoutState$.next(state);
  }

  getShoppingCart(id?: string): Observable<ShoppingCart> {
    //Todo: check if request to backend needed
    if (id) {
      return this.getPersistedCart(id);
    } else {
      return this.getCart();
    }
  }

  updateShoppingCart(cart: ShoppingCart): Observable<ShoppingCart> {
    return fromPromise(this.saveCartToLocalStorage(cart));
  }

  addItemToShoppingCartFromProduct(
    product: ProductDetail,
    quantity: number,
    referenceMaterial?: string,
    showNotification?: boolean,
  ): Observable<void> {
    return this.addItemToShoppingCart(product, quantity, referenceMaterial, showNotification).pipe(
      tap((cart) => this.store.dispatch(shoppingCartSuccess(cart))),
      map(() => void 0),
    );
  }

  addItemToShoppingCart(
    product: ProductDetail,
    quantity: number,
    referenceMaterial?: string,
    showNotification?: boolean,
  ): Observable<ShoppingCart> {
    return fromPromise(this.getLocalCart()).pipe(
      switchMap((cart) => {
        const indexToUpdate = cart.items.findIndex((item) => item.product.materialNumber === product.materialNumber);
        const item = cart.items[indexToUpdate];
        const remainingQuantity = 99 - (item?.quantity ?? 0);
        const addedQuantity = Math.min(remainingQuantity, quantity);

        if (remainingQuantity <= 0 && quantity > 0) {
          this.toastService.open(this.translateService.instant('common.max_quantity_reached'), 'warning', {
            timeOut: 4000,
          });

          return of(cart);
        } else {
          const mutableCart = createDeepMutableClone(cart);

          const newItem: ShippingItem = {
            quantity: Math.min(quantity, 99),
            commission: cart.commission ?? '',
            product: product,
            referenceMaterial,
          };

          if (indexToUpdate >= 0) {
            newItem.commission = item.commission;
            newItem.quantity = Math.min(item.quantity + quantity, 99);
            if (newItem.quantity > 0) {
              mutableCart.items[indexToUpdate] = newItem;
            } else {
              mutableCart.items.splice(indexToUpdate, 1);
            }
          } else {
            if (newItem.quantity > 0) {
              mutableCart.items.push(newItem);
            }
          }

          mutableCart.itemsQuantity = this.calculateTotalQuantity(mutableCart);
          mutableCart.currency = product.currency;
          mutableCart.calculated = false;

          return fromPromise(
            this.saveCartToLocalStorage(mutableCart).then((savedCart) => {
              if (showNotification) {
                const translateKey =
                  addedQuantity > 1 ? 'common.added_multiple_to_cart' : 'common.added_single_to_cart';
                this.toastService.open(
                  this.translateService.instant(translateKey, {
                    quantity: addedQuantity,
                  }),
                  'success',
                );
              }
              return savedCart;
            }),
          );
        }
      }),
    );
  }

  calculateShoppingCartPriceFromProduct(): Observable<void> {
    this.store.dispatch(shoppingCartPriceLoading());
    return this.calculateShoppingCartPrice().pipe(
      tap((cart) => this.store.dispatch(shoppingCartPriceSuccess(cart))),
      map(() => void 0),
    );
  }

  /**
   * Persists the cart in the backend and calculates the price of the shopping cart. If the cart is empty or user is not logged in, it resets the prices to 0.
   */
  calculateShoppingCartPrice(): Observable<ShoppingCart> {
    //Todo: check if user logged in. If not, set the prices to 0
    return fromPromise(this.getLocalCart()).pipe(
      switchMap((shoppingCart) => {
        if (shoppingCart.items.length === 0) {
          const updatedCart = this.resetPrices(shoppingCart);
          this.saveCartToLocalStorage(updatedCart);
          return of(updatedCart);
        }

        let request = of(shoppingCart);

        if (shoppingCart.calculated) {
          return request;
        }

        if (!shoppingCart.id) {
          request = this.postCartRequest(shoppingCart);
        } else {
          request = this.putCartRequest(shoppingCart.id, shoppingCart);
        }

        return request.pipe(
          switchMap((cart) => {
            return this.putCalculateCartRequest(cart.id ?? 0, shoppingCart);
          }),
        );
      }),
    );
  }

  //Todo: remove method and use updateShoppingCart instead
  addPickupLocationToShoppingCart(pickupLocation: PickupLocation): Observable<ShoppingCart> {
    return fromPromise(this.getLocalCart()).pipe(
      switchMap((cart) => {
        cart.deliveryInfo.pickupLocation = pickupLocation;
        return fromPromise(this.saveCartToLocalStorage(cart));
      }),
    );
  }

  /**
   * Checks if the user is logged in and opens the checkout page in a new tab.
   */
  checkoutShoppingCart(): Observable<ShoppingCart> {
    if (this.authService.hasValidCsrfToken()) {
      const request = fromPromise(this.getLocalCart()).pipe(switchMap((cart) => this.sendShoppingCartToBackend(cart)));

      return request.pipe(
        tap((cart) => {
          if (cart.items.length !== 0) {
            let url = `${environment.backend.shopUrl}?shopApiContext=viparts&redirectToCheckout=true&vipartsCart=${cart.id}`;
            if (this.authService.hasValidCsrfToken()) {
              url += `&sessionid=1#token=${this.authService.getCsrfToken()}`;
            }
            this.browserService.open(url, '_blank', 'Viessmann PartnerShop');
            this.setCheckoutState({ checkoutInProgress: true });
          }
        }),
      );
    } else {
      this.authService.login('cart?checkout=true');
      return this.getShoppingCart();
    }
  }

  /**
   * Persists the shopping cart to the backend and shares it with other users. No authentication required.
   */
  shareShoppingCart(): Observable<ShoppingCart> {
    const request = fromPromise(this.getLocalCart()).pipe(switchMap((cart) => this.sendShoppingCartToBackend(cart)));

    return request.pipe(
      tap(async (result) => {
        const template = this.translateService.instant('shopping_cart.share.text');
        const text = CartShareFactory.build(result, template);
        const title = this.translateService.instant('shopping_cart.share.title');
        const dialogTitle = this.translateService.instant('shopping_cart.share.dialog_title');
        const canShare = await Share.canShare();
        // If we cannot share, we copy the text to the clipboard
        if (!canShare.value) {
          await Clipboard.write({
            string: text,
          }).then(() => {
            const toast = this.translateService.instant('shopping_cart.share.copy_to_clipboard');
            return this.toastService.open(toast, 'info');
          });
        } else {
          await Share.share({ title, text, dialogTitle });
        }
      }),
    );
  }

  resetShoppingCart(): Observable<ShoppingCart> {
    return this.catalogService.getCurrentCatalog().pipe(
      switchMap((catalog) => {
        return this.createNewCartContent(catalog.salesOrg, catalog.language).pipe(
          tap((cart) => {
            this.saveCartToLocalStorage(cart);
          }),
        );
      }),
    );
  }

  private getCart(): Observable<ShoppingCart> {
    return this.catalogService.getCurrentCatalog().pipe(
      switchMap((catalog) =>
        fromPromise(this.getLocalCart()).pipe(
          switchMap((cart) => {
            let cartObservable;
            if (cart) {
              if (cart.calculated) {
                cartObservable = of(cart);
              } else {
                cartObservable = this.calculateShoppingCartPrice();
              }
            } else {
              cartObservable = this.createNewCartContent(catalog.salesOrg, catalog.language);
            }
            return cartObservable.pipe(
              tap((cart) => {
                this.saveCartToLocalStorage(cart);
              }),
            );
          }),
        ),
      ),
    );
  }

  private getPersistedCart(cartId: string): Observable<ShoppingCart> {
    return this.catalogService
      .getCurrentCatalog()
      .pipe(
        switchMap((catalog) =>
          this.getShoppingCartFromBackend(cartId, catalog.salesOrg, catalog.language).pipe(
            tap((updatedCart) => fromPromise(this.saveCartToLocalStorage(updatedCart))),
          ),
        ),
      );
  }

  private getShoppingCartFromBackend(id: string, salesOrg: string, locale: string) {
    return this.http.get<ShoppingCart>(environment.backend.url + `/cart/${id}`, {
      params: {
        salesOrg: salesOrg,
        locale: locale,
      },
    });
  }

  private sendShoppingCartToBackend(cart: ShoppingCart) {
    let request;

    if (cart.id) {
      request = this.putCartRequest(cart.id, cart);
    } else {
      request = this.postCartRequest(cart);
    }

    return request.pipe(
      map((response) => this.addCartId(cart, response.id)),
      switchMap(async (c) => this.saveCartToLocalStorage(c)),
    );
  }

  private postCartRequest(shoppingCart: ShoppingCart) {
    return this.http.post<ShoppingCart>(environment.backend.url + `/cart`, shoppingCart);
  }

  private putCartRequest(id: number, shoppingCart: ShoppingCart) {
    return this.http.put<ShoppingCart>(environment.backend.url + `/cart/${id}`, shoppingCart).pipe(
      catchError((error) => {
        if (error.staus === 404) {
          console.error('Cart with id ' + shoppingCart.id + ' not found: ', error);
          const mutableCart = createDeepMutableClone(shoppingCart);
          mutableCart.id = undefined;
          this.saveCartToLocalStorage(mutableCart);
          return this.postCartRequest(mutableCart);
        } else {
          throw error;
        }
      }),
    );
  }

  private putCalculateCartRequest(id: number, shoppingCart: ShoppingCart): Observable<ShoppingCart> {
    return this.http.put<ShoppingCart>(environment.backend.url + `/cart/${id}/calculate`, shoppingCart).pipe(
      map((calculatedCart) => {
        const updatedCart = this.updatePrices(calculatedCart, shoppingCart);
        updatedCart.id = calculatedCart.id;
        this.saveCartToLocalStorage(updatedCart);
        return updatedCart;
      }),
      catchError(() => {
        const noPricesCart = this.resetPrices(shoppingCart);
        noPricesCart.calculated = false;
        this.saveCartToLocalStorage(noPricesCart);
        return of(noPricesCart);
      }),
    );
  }

  private addCartId(cart: ShoppingCart, id: number | undefined) {
    cart.id = id;
    return cart;
  }

  private async saveCartToLocalStorage(cart: ShoppingCart): Promise<ShoppingCart> {
    await this.storage.set('shopping_cart', cart);
    return cart;
  }

  private getLocalCart() {
    return this.storage.get<ShoppingCart>('shopping_cart');
  }

  private calculateTotalQuantity(cart: ShoppingCart): number {
    return cart.items.reduce((total, item) => total + item.quantity, 0);
  }

  private updatePrices(source: ShoppingCart, target: ShoppingCart) {
    const mutableCart = createDeepMutableClone(target);
    mutableCart.currency = source.currency;
    mutableCart.netTotal = source.netTotal;
    mutableCart.grossTotal = source.grossTotal;
    mutableCart.totalAmount = source.totalAmount;
    mutableCart.tax = source.tax;
    mutableCart.taxRate = source.taxRate;
    mutableCart.onlineDiscount = source.onlineDiscount;
    mutableCart.calculated = true;

    return mutableCart;
  }

  private resetPrices(cart: ShoppingCart) {
    const mutableCart = createDeepMutableClone(cart);
    mutableCart.netTotal = 0;
    mutableCart.grossTotal = 0;
    mutableCart.totalAmount = 0;
    mutableCart.tax = 0;
    mutableCart.taxRate = 0;
    mutableCart.onlineDiscount = 0;
    mutableCart.calculated = true;

    return mutableCart;
  }

  private createNewCartContent(salesOrg: string, language: string): Observable<ShoppingCart> {
    return of({
      creationDate: dayjs().format('YYYY-MM-DD'),
      salesOrg: salesOrg,
      language: language,
      currency: '',
      items: [],
      itemsQuantity: 0,
      netTotal: 0,
      grossTotal: 0,
      totalAmount: 0,
      onlineDiscount: 0,
      tax: 0,
      taxRate: 0,
      calculated: false,
      ordered: false,
      deliveryInfo: {
        deliveryDate: dayjs().format('YYYY-MM-DD'),
        deliveryType: DeliveryType.shipping,
      },
    });
  }

  private startCartPolling(timeoutMs = 300000) {
    this.pollingSub?.unsubscribe();

    this.pollingSub = fromPromise(this.getLocalCart())
      .pipe(
        switchMap((cart) => {
          const cartId = cart.id ? cart.id.toString() : undefined;
          const salesOrg = cart.salesOrg;
          const language = cart.language;

          if (!cartId || !salesOrg || !language) {
            return of(null);
          }

          return interval(5000).pipe(
            switchMap(() => this.getShoppingCartFromBackend(cartId, salesOrg, language)),
            takeWhile((response) => !response.ordered, true),
            takeUntil(timer(timeoutMs)),
          );
        }),
      )
      .subscribe((response) => {
        if (response?.ordered) {
          this.store.dispatch(shoppingCartReset());
          this.setCheckoutState({ checkoutInProgress: false });
        }
      });
  }
}
